/**
 * Thin helpers for sending custom telemetry to Application Insights from any bundle.
 *
 * The Application Insights SDK is initialized in application-insights.ts, which is a separate
 * bundle that exposes the instance on window.appInsights. These helpers read it lazily so they
 * can be imported anywhere (including the Blazor bundle) without pulling in a second copy of
 * the SDK. They never throw, and are no-ops if Application Insights is not loaded.
 *
 * Never pass personal data (names, emails, phone numbers, free text) as properties.
 */

export type TelemetryProperties = Record<
  string,
  string | number | boolean | undefined | null
>;

const SEVERITY_ERROR = 3;

const cleanProperties = (
  properties?: TelemetryProperties
): Record<string, string | number | boolean> | undefined => {
  if (!properties) {
    return undefined;
  }

  const cleaned: Record<string, string | number | boolean> = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      cleaned[key] = value;
    }
  });

  return cleaned;
};

export const trackAppInsightsEvent = (
  name: string,
  properties?: TelemetryProperties
): void => {
  try {
    window.appInsights?.trackEvent({ name }, cleanProperties(properties));
  } catch {
    // Telemetry must never break the page
  }
};

/** Sends any buffered telemetry now, e.g. right before the page reloads. */
export const flushAppInsights = (): void => {
  try {
    window.appInsights?.flush?.();
  } catch {
    // Telemetry must never break the page
  }
};

export type BookingFlow = "Provet" | "Vetstoria";

export interface BookingStepTelemetry {
  flow: BookingFlow;
  /** Step name, e.g. SelectClinic, ContactDetails, Confirmed */
  step: string;
  stepNumber?: number | string;
  /** Whether the step was completed (as opposed to entered) */
  complete?: boolean;
  /** Booking session id (ac_booking_sid) */
  bookingSessionId?: string;
  /** CMS clinic id */
  clinicId?: string | number;
  /** PMS (Provet department / Vetstoria) clinic id */
  clinicPmsId?: string | number;
}

/**
 * Sends a BookingStep custom event with a consistent set of properties for both booking flows.
 * Contains no personal data.
 */
export const trackBookingStepEvent = (data: BookingStepTelemetry): void => {
  trackAppInsightsEvent("BookingStep", {
    Flow: data.flow,
    Step: data.step,
    StepNumber: data.stepNumber === undefined ? undefined : String(data.stepNumber),
    Complete: data.complete,
    BookingSessionId: data.bookingSessionId,
    ClinicId: data.clinicId === undefined ? undefined : String(data.clinicId),
    ClinicPmsId: data.clinicPmsId === undefined ? undefined : String(data.clinicPmsId)
  });
};

export const trackAppInsightsException = (
  error: unknown,
  properties?: TelemetryProperties
): void => {
  try {
    window.appInsights?.trackException(
      {
        exception: error instanceof Error ? error : new Error(String(error)),
        severityLevel: SEVERITY_ERROR
      },
      cleanProperties(properties)
    );
  } catch {
    // Telemetry must never break the page
  }
};
